import {
  FormItem,
  type ErrorMessageType,
} from "@freightsimple/ui-core/FormLayout/FormItem";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import type { InputWidth } from "@freightsimple/ui-core/Controls/inputWidth";
import { useOnce } from "@/Hooks/useOnce";
import type { ReactNode } from "react";
import type { UseValidateReferenceNumberReturn } from "../Hooks/useReferenceNumbersValidate";
import {
  REFERENCE_NUMBER_READABLE_MAX_CHARS,
  referenceNumbersIsTooLongForBol,
} from "../Helpers/referenceNumbersIsTooLongForBol";

function lengthWarning_() {
  return (
    <span data-testid="reference-number-length-warning">
      Over {REFERENCE_NUMBER_READABLE_MAX_CHARS} characters prints small on the
      bill of lading. Shorten it if you can, and put any descriptions or
      instructions in the notes.
    </span>
  );
}

interface ReferenceNumberInputProps {
  validation: UseValidateReferenceNumberReturn;
  value: string | undefined;
  onChange: (newValue: string) => void;
  label?: ReactNode;
  placeholder?: string | undefined;
  width?: InputWidth | undefined;
  maxLength?: number | undefined;
  errorMessage?: ErrorMessageType;
  /** Show the validation message straight away rather than waiting for a blur. */
  forceValidation?: boolean | undefined;
  "data-testid"?: string | undefined;
}

export function ReferenceNumbersInput({
  validation,
  value,
  onChange,
  label,
  placeholder,
  width = "full",
  maxLength,
  errorMessage,
  forceValidation = true,
  "data-testid": dataTestId,
}: ReferenceNumberInputProps) {
  const { result, validate, isLoading } = validation;

  useOnce(() => {
    validate(value);
  });

  function handleChange(newValue: string) {
    onChange(newValue);
    validate(newValue);
  }

  // A reused or long reference number is a warning, not an error — the booking
  // still goes through, so it can't block. Several references in one field are
  // legitimate, which is why length only ever warns. An explicit errorMessage
  // outranks both.
  const warning = errorMessage
    ? undefined
    : !result.isValid && result.message
      ? result.message
      : referenceNumbersIsTooLongForBol(value)
        ? lengthWarning_()
        : undefined;

  return (
    <ShowFormErrors show={forceValidation}>
      <FormItem label={label} error={errorMessage} warning={warning}>
        <TextInput
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          width={width}
          maxLength={maxLength}
          loading={isLoading}
          data-testid={dataTestId}
        />
      </FormItem>
    </ShowFormErrors>
  );
}
